/* @flow */
/**
 * Redux Toolkit slice for the Air Freight Override feature.
 *
 * This module owns all state, reducers and async thunks used by the
 * Air Freight Override and Adjustment screens, including fetching results,
 * paginating adjustment data and updating pending approvals.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import {
  updateDialogStates,
  toggleSpinner
} from '../dialogStates/dialogStatesSlice';
import getflattenResponse from '../../helpers/common';
import airFreightReportFieldMapping from '../../constants/fieldMappings/airFreightReportFieldMapping';
import { initialAirFreightAdjustmentSearchFields } from '../../constants/staticData';
import appConfig from '../../appConfig';

// Unique identifier for this slice; used as the Redux state key and thunk prefix.
export const name = 'airFreightOverride';

function createInitialState() {
  return {
    airFreightOverrideScreenData: [],
    airFreightOverrideAdjustmentScreenData: [],
    airFreightSubtableResults: {},
    noAdjustmentResults: false,
    initialAdjustmentFieldsState: { ...initialAirFreightAdjustmentSearchFields },
    afAdjustmentTotalCount: 0,
    page: 0,
    offset: 0
  };
}

export const initialState = createInitialState();

/**
 * Reducer: stores the results for the main Air Freight Override screen.
 */
function updateAirFreightOverrideScreen(state: Object, action: Object) {
  // UPDATE_AIRFREIGHT_OVERRIDE_SCREEN_RESULTS
  const payload = {
    data: action.payload
  };
  return {
    ...state,
    airFreightOverrideScreenData: payload.data
  };
}

/**
 * Reducer: stores the Air Freight Override adjustment results.
 *
 * For each purchase order (poId) only the most recently created record is
 * marked as editable; every row sharing that record's afOverrideId is then
 * flagged editable so the UI can allow edits on the latest version only.
 */
function updateAirFreightOverrideAdjustmentScreen(state: Object, action: Object) {
  // UPDATE_AIRFREIGHT_OVERRIDE_ADJUSTMENT_SCREEN_RESULTS
  const afData = action.payload.objects;
  // Determine, per PO, the latest record (by createdDate) and flag it editable.
  const editableItems = [...new Set(afData.map((item) => item.poId))].map(
    (poId) => {
      let latestItem = {};
      afData.filter((item) => item.poId === poId).forEach(
        (item) => {
          if (
            latestItem.createdDate === undefined
            || new Date(item.createdDate).getTime() > new Date(latestItem.createdDate).getTime()
          ) {
            latestItem = item;
          }
        }
      );
      return { ...latestItem, afOverrideId: latestItem.afOverrideId, isEditable: true };
    }
  );

  // Propagate the editable flag to every row matching an eligible afOverrideId.
  const eligibleEditPoId = new Set(editableItems.map((item) => item?.afOverrideId));
  const modifiedAfData = afData.map((item) => {
    if (eligibleEditPoId.has(item.afOverrideId)) {
      return {
        ...item,
        isEditable: true
      };
    }
    return item;
  });
  const payload = {
    data: action.payload
  };
  return {
    ...state,
    airFreightOverrideAdjustmentScreenData: modifiedAfData,
    noAdjustmentResults: modifiedAfData.length === 0,
    afAdjustmentTotalCount: payload.data.pages.totalResources
  };
}

/**
 * Reducer: persists the current adjustment search form values.
 */
function updateAFAdjustmentInputData(state: Object, action: Object) {
  // UPDATE_AIRFREIGHT_ADJUSTMENT_REQUEST_DATA
  return {
    ...state,
    initialAdjustmentFieldsState: action.payload
  };
}

/**
 * Reducer: clears adjustment results and resets search form values.
 */
function clearAirFreightAdjustmentData(state: Object, action: Object) {
  // CLEAR_AIRFREIGHT_ADJUSTMENT_DATA
  return {
    ...state,
    airFreightOverrideAdjustmentScreenData: [],
    initialAdjustmentFieldsState: action.payload,
    noAdjustmentResults: false
  };
}

/**
 * Reducer: stores the flattened sub-table (GAC history) results for a row.
 */
function updateAirFreightOverrideSubtableResults(state: Object, action: Object) {
  // UPDATE_AIR_FREIGHT_OVERRIDE_SUBTABLE_RESULTS
  const payload = {
    data: getflattenResponse(action.payload),
    dataNext: null
  };
  return {
    ...state,
    airFreightOverrideSubtableResults: payload.data
  };
}

/**
 * Reducer: updates the current adjustment page index.
 * Accepts either a page object ({ page }) or a raw page value.
 */
function updateAfAdjustmentPage(state: Object, action: Object) {
  // UPDATE_AF_ADJUSTMENT_PAGE

  return {
    ...state,
    page: action.payload?.page ? action.payload?.page : action.payload
  };
}

/**
 * Reducer: updates the current adjustment offset used for pagination.
 * Accepts either an offset object ({ offset }) or a raw offset value.
 */
function updateAfAdjustmentPageOffset(state: Object, action: Object) {
  // UPDATE_AF_ADJUSTMENT_PAGE_OFFSET

  return {
    ...state,
    offset: action.payload?.offset ? action.payload?.offset : action.payload
  };
}

/**
 * Groups every synchronous reducer for registration with createSlice.
 */
function createReducers() {
  return {
    updateAirFreightOverrideScreen,
    updateAirFreightOverrideAdjustmentScreen,
    updateAFAdjustmentInputData,
    clearAirFreightAdjustmentData,
    updateAirFreightOverrideSubtableResults,
    updateAfAdjustmentPage,
    updateAfAdjustmentPageOffset
  };
}

export const reducers = createReducers();
// Create the slice, generating synchronous action creators from the reducers.
export const slice = createSlice({ name, initialState, reducers });

// Auto-generated synchronous action creators for the reducers above.
const actions = { ...slice.actions };

/**
 * Async thunk: runs an adjustment search and loads the first page of results.
 * Resets the page to 0, stores the search input and updates the offset from the
 * API's "next" page cursor.
 */
function getAirFreightOverrideAdjustmentResults() {
  return createAsyncThunk(
    `${name}/getAirFreightOverrideAdjustmentResults`,
    async ({
      searchQuery,
      inputValues,
      callback
    }, { dispatch }) => {
      const offset = 0;
      // Default to the GC geo filter when no explicit search query is provided.
      const searchField = searchQuery || '&filter=geoCode(GC)';
      const query = `?count=${appConfig.airFrieghtAdjustmentCount}${searchField}&offset=${offset}`;
      const payload = {
        data: {}
      };
      dispatch(toggleSpinner(true));
      dispatch(actions.updateAFAdjustmentInputData(inputValues));
      dispatch(actions.updateAfAdjustmentPage(0));
      dispatch(updateDialogStates({ conversationSearchSpinner: true }));
      request({
        api: 'getAirFreightOverrideAdjustment',
        routeParams: { query },
        method: 'get'
      }, dispatch).then((response) => {
        dispatch(toggleSpinner(false));
        dispatch(updateDialogStates({ conversationSearchSpinner: false }));
        payload.data = response.data;
        // Extract the offset value from the "next" page cursor for pagination.
        dispatch(actions.updateAfAdjustmentPageOffset(payload.data.pages.next.split(',')[0].split('=')[1]));
        dispatch(actions.updateAirFreightOverrideAdjustmentScreen(payload.data));
        if (callback) {
          callback(response.data);
        }
      }).catch((error) => {
        dispatch(toggleSpinner(false));
        dispatch(updateDialogStates({ conversationSearchSpinner: false }));
        console.log(error);
        if (callback) {
          callback(null, error);
        }
      });
    }
  );
}

/**
 * Async thunk: loads the current user's pending Air Freight Override records
 * for the main override screen.
 */
function getAirFreightOverrideResults() {
  return createAsyncThunk(
    `${name}/getAirFreightOverrideResults`,
    async ({
      callback
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      const state = getState();
      const status = 'Pending';
      // Identify the requesting user from the parsed auth token.
      const id = state?.auth?.parsedToken?.accessToken?.claims?.legacy_username;
      request({
        api: 'getAirFreightOverride',
        routeParams: { id, status },
        method: 'get',
        cancellable: true
      }, dispatch, getState)
        .then((response) => {
          const flattenedResponse = getflattenResponse(response.data);
          dispatch(actions.updateAirFreightOverrideScreen(flattenedResponse.objects));
          dispatch(toggleSpinner(false));
          if (callback) {
            callback(response);
          }
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          console.log(error);
          callback(error);
        });
    }
  );
}

/**
 * Async thunk: submits an update for one or more pending approval records.
 */
function updatePendingApproval() {
  return createAsyncThunk(
    `${name}/updatePendingApproval`,
    async ({
      data,
      callback
    }, { getState, dispatch }) => {
      if (data) {
        const updateRequestBody = {
          type: 'update',
          airFreightData: data
        };
        dispatch(toggleSpinner(true));
        request({
          api: 'updatePendingApproval',
          method: 'put',
          data: updateRequestBody,
          cancellable: true
        }, dispatch, getState)
          .then((response) => {
            dispatch(toggleSpinner(false));
            callback(response.data);
          })
          .catch((error) => {
            dispatch(toggleSpinner(false));
            callback(null, error);
          });
      }
    }
  );
}

/**
 * Async thunk: fetches the sub-table (GAC history) data for an expanded row,
 * filtering the report search results down to the matching row id.
 */
function fetchAirFreightOverrideSubTable() {
  return createAsyncThunk(
    `${name}/fetchAirFreightOverrideSubTable`,
    async ({
      rowId,
      poNumber,
      itemNumber,
      callback
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      // Fields requested from the air freight report search.
      const fields = [
        airFreightReportFieldMapping.AIR_FREIGHT_PO_NUMBER,
        airFreightReportFieldMapping.AIR_FREIGHT_ITEM_NUMBER,
        'dpomGacHistoryData'
      ];
      // Constrain the search to the given PO number and item number.
      const requestData = [
        { fieldName: 'poHeader.poNumber', operator: '=', fieldValue: [poNumber] },
        { fieldName: 'poLine.itemNumber', operator: '=', fieldValue: [itemNumber] }
      ];
      const requestBody = {
        search: requestData,
        offset: '0',
        fields: [...fields],
        count: appConfig.SearchThresholdLimit
      };
      request({
        api: 'airFreightReportSearch',
        method: 'post',
        data: requestBody,
        cancellable: true
      }, dispatch, getState)
        .then((response) => {
          dispatch(toggleSpinner(false));
          // Keep only the record matching the expanded row.
          const filteredData = response.data.objects.filter((x) => x.id === rowId);
          callback(filteredData);
          dispatch(actions.updateAirFreightOverrideSubtableResults(filteredData));
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          callback(null, error);
        });
    }
  );
}

/**
 * Async thunk: loads the next page of adjustment results.
 * Uses the current offset from state and advances the offset using the
 * response's "next" cursor (falling back to "prev" when "next" is absent).
 */
function fetchAfAdjustmentNextResultSet() {
  return createAsyncThunk(
    `${name}/fetchAfAdjustmentNextResultSet`,
    async ({
      searchQuery,
      inputValues,
      callback
    }, { dispatch, getState }) => {
      const state = getState();
      const offset = Number(state.afOverride.offset);
      const searchField = searchQuery || '&filter=geoCode(GC)';
      const query = `?count=${appConfig.airFrieghtAdjustmentCount}${searchField}&offset=${offset}`;
      const payload = {
        data: {}
      };
      dispatch(toggleSpinner(true));
      dispatch(actions.updateAFAdjustmentInputData(inputValues));
      dispatch(updateDialogStates({ conversationSearchSpinner: true }));
      request({
        api: 'getAirFreightOverrideAdjustment',
        routeParams: { query },
        method: 'get'
      }, dispatch).then((response) => {
        dispatch(toggleSpinner(false));
        dispatch(updateDialogStates({ conversationSearchSpinner: false }));
        payload.data = response.data;
        const nextOffset = payload.data.pages?.next?.split(',')[0].split('=')[1];
        const prevOffset = payload.data.pages?.prev?.split(',')[0].split('=')[1];
        // Prefer the "next" cursor; fall back to "prev" on the last page.
        dispatch(actions.updateAfAdjustmentPageOffset(nextOffset || prevOffset));
        dispatch(actions.updateAirFreightOverrideAdjustmentScreen(payload.data));
        if (callback) {
          callback(response.data);
        }
      }).catch((error) => {
        dispatch(toggleSpinner(false));
        dispatch(updateDialogStates({ conversationSearchSpinner: false }));
        console.log(error);
        if (callback) {
          callback(null, error);
        }
      });
    }
  );
}

/**
 * Async thunk: loads the previous page of adjustment results.
 * Uses the current offset from state and moves the offset using the
 * response's "prev" cursor (falling back to "next" when "prev" is absent).
 */
function fetchAfAdjustmentPrevResultSet() {
  return createAsyncThunk(
    `${name}/fetchAfAdjustmentPrevResultSet`,
    async ({
      searchQuery,
      inputValues,
      callback
    }, { dispatch, getState }) => {
      const state = getState();
      const offset = Number(state.afOverride.offset);
      const searchField = searchQuery || '&filter=geoCode(GC)';
      const query = `?count=${appConfig.airFrieghtAdjustmentCount}${searchField}&offset=${offset}`;
      const payload = {
        data: {}
      };
      dispatch(toggleSpinner(true));
      dispatch(actions.updateAFAdjustmentInputData(inputValues));
      dispatch(updateDialogStates({ conversationSearchSpinner: true }));
      request({
        api: 'getAirFreightOverrideAdjustment',
        routeParams: { query },
        method: 'get'
      }, dispatch).then((response) => {
        dispatch(toggleSpinner(false));
        dispatch(updateDialogStates({ conversationSearchSpinner: false }));
        payload.data = response.data;
        const nextOffset = payload.data.pages?.next?.split(',')[0].split('=')[1];
        const prevOffset = payload.data.pages?.prev?.split(',')[0].split('=')[1];
        // Prefer the "prev" cursor; fall back to "next" on the first page.
        dispatch(actions.updateAfAdjustmentPageOffset(prevOffset || nextOffset));
        dispatch(actions.updateAirFreightOverrideAdjustmentScreen(payload.data));
        if (callback) {
          callback(response.data);
        }
      }).catch((error) => {
        dispatch(toggleSpinner(false));
        dispatch(updateDialogStates({ conversationSearchSpinner: false }));
        console.log(error);
        if (callback) {
          callback(null, error);
        }
      });
    }
  );
}

/**
 * Instantiates every async thunk and exposes them as extra actions.
 */
function createExtraActions() {
  return {
    getAirFreightOverrideAdjustmentResults: getAirFreightOverrideAdjustmentResults(),
    getAirFreightOverrideResults: getAirFreightOverrideResults(),
    updatePendingApproval: updatePendingApproval(),
    fetchAirFreightOverrideSubTable: fetchAirFreightOverrideSubTable(),
    fetchAfAdjustmentNextResultSet: fetchAfAdjustmentNextResultSet(),
    fetchAfAdjustmentPrevResultSet: fetchAfAdjustmentPrevResultSet()
  };
}

export const extraActions = createExtraActions();

export const airFreightOverrideActions = { ...actions, ...extraActions };
// The reducer to register in the Redux store.
export const airFreightOverrideReducer = slice.reducer;
